import { JxsComponent } from '@jxs/component';

/**
 * Countdown Timer 设计说明 / 维护注意事项
 * ------------------------------------------------------------
 * 目标：
 * 1. 数字必须“视觉等宽”，不能依赖当前字体是否支持 tabular-nums。
 * 2. 倒计时整体宽度必须跟随容器宽度，刚好铺满容器。
 * 3. 允许主题配置字体粗细、颜色、渐变/图片文字背景、结束图片/结束文案。
 *
 * 核心宽度模型：
 * - JS 把每个数字拆成两层 DOM：
 *   - .timer__digit-slot：等宽槽位，负责布局宽度。
 *   - .timer__digit-glyph：真实字符，居中放在槽位里。
 * - 容器宽度不是由字体自然撑开，而是反推单个槽位宽度：
 *   slotSize = containerWidth / (visibleDigitCount + separatorWeight * visibleSeparatorCount)
 * - CSS 中每个单位宽度 = digitCount * slotSize，冒号宽度 = separatorWeight * slotSize。
 * - 维护时不要随意给 .countdown-timer__time / .countdown-timer__item / .countdown-timer__separator
 *   增加 gap、margin、额外 padding 或 border；如果必须增加，必须同步改上面的宽度模型，
 *   否则会破坏“刚好铺满容器”。
 *
 * 字体无关的等宽实现：
 * - 不使用 font-variant-numeric: tabular-nums，因为并非所有字体都支持。
 * - 使用离屏 measurer 复制当前数字的 font-family / weight / style / letter-spacing 等样式，
 *   在 MEASURE_FONT_SIZE 下测量 0-9 的最大字宽。
 * - 再按比例计算真实字号：
 *   digitFontSize = slotSize * MEASURE_FONT_SIZE / maxDigitWidth
 * - 这样即使字体里 “1” 很窄、“8” 很宽，也会以最宽数字为基准，确保任何数字都不会溢出槽位。
 *
 * 重新计算时机：
 * - 容器尺寸变化：ResizeObserver。
 * - 字体加载完成或后续字体替换：document.fonts.ready / loadingdone。
 * - 倒计时数值变化：位数可能变化，例如 DAY 从 10 变 9、隐藏 DAY。
 * - 所有重算统一进入 requestAnimationFrame，避免同一帧内多次读写布局。
 *
 * 结束状态：
 * - Liquid 把结束图片/文案放在 template[data-countdown-end-message] 中，首屏不渲染。
 * - 倒计时结束后 JS 克隆 template 内容，旧时间淡出，新内容淡入。
 * - 切换期间临时锁定 inner 的 min-height，避免旧 DOM 删除时页面高度瞬间跳动。
 *
 * 渐变/图片文字背景：
 * - 每个 glyph 和冒号都使用 background-clip:text。
 * - JS 会根据元素相对整条倒计时的 left 偏移同步 background-position，
 *   让背景在所有数字/冒号之间连续，而不是每个字符各自从 0 开始重复。
 */
const DAY_SECONDS = 24 * 60 * 60;
const HOUR_SECONDS = 60 * 60;
const MINUTE_SECONDS = 60;
const SEPARATOR_WEIGHT = 1.45;
const MEASURE_FONT_SIZE = 100;
const DIGIT_SAMPLE = '0123456789';
const DEFAULT_DATETIME_PATTERN = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2})(?::(\d{1,2}))?(?::(\d{1,2}))?)?$/;

/**
 * @typedef {'day' | 'hour' | 'minute' | 'second'} CountdownUnitKey
 */

/**
 * @typedef {{
 *   day: string;
 *   hour: string;
 *   minute: string;
 *   second: string;
 *   totalSeconds: number;
 * }} CountdownParts
 */

/** @type {{ key: CountdownUnitKey; label: string }[]} */
const UNIT_CONFIGS = [
  { key: 'day', label: 'DAY' },
  { key: 'hour', label: 'HR' },
  { key: 'minute', label: 'MIN' },
  { key: 'second', label: 'SEC' },
];

const FONT_PROPERTIES = ['font-family', 'font-weight', 'font-style', 'font-stretch', 'letter-spacing', 'text-transform'];

const measurer = document.createElement('span');
measurer.setAttribute('aria-hidden', 'true');
Object.assign(measurer.style, {
  position: 'fixed',
  left: '-99999px',
  top: '-99999px',
  visibility: 'hidden',
  whiteSpace: 'pre',
  lineHeight: '1',
  display: 'inline-block',
  pointerEvents: 'none',
});
document.body.appendChild(measurer);

class JxsCountdownTimer extends JxsComponent {
  static get observedAttributes() {
    return ['data-datetime', 'data-test'];
  }

  /** @type {Date | null} */
  #targetDate = null;

  /** @type {number | undefined} */
  #timeoutId;

  /** @type {number | undefined} */
  #frameId;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {HTMLElement | null} */
  #inner = null;

  /** @type {HTMLElement | null} */
  #time = null;

  /** @type {Partial<Record<CountdownUnitKey, HTMLElement>>} */
  #valueElements = {};

  /** @type {Partial<Record<CountdownUnitKey, string>>} */
  #lastValues = {};

  /** @type {boolean} */
  #ending = false;

  connectedCallback() {
    super.connectedCallback();

    this.#inner = this.querySelector('[ref="inner"]') ?? this;
    this.#targetDate = this.#resolveTargetDate();

    this.setAttribute('role', 'timer');
    this.setAttribute('aria-live', 'polite');
    this.style.setProperty('--countdown-separator-weight', String(SEPARATOR_WEIGHT));

    if (!this.#targetDate) {
      this.#renderInvalidState();
      return;
    }

    this.#renderShell();
    this.#start();

    this.#resizeObserver = new ResizeObserver(() => this.#scheduleEqualize());
    this.#resizeObserver.observe(this);

    if (document.fonts) {
      document.fonts.ready.then(() => this.#scheduleEqualize());
      document.fonts.addEventListener?.('loadingdone', this.#scheduleEqualize);
    }
  }

  disconnectedCallback() {
    this.#stop();
    this.#resizeObserver?.disconnect();

    if (this.#frameId !== undefined) {
      cancelAnimationFrame(this.#frameId);
      this.#frameId = undefined;
    }

    if (document.fonts) {
      document.fonts.removeEventListener?.('loadingdone', this.#scheduleEqualize);
    }

    super.disconnectedCallback();
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || !this.isConnected) return;

    this.#targetDate = this.#resolveTargetDate();
    this.#lastValues = {};
    this.#ending = false;
    this.hidden = false;
    this.#stop();

    if (!this.#targetDate) {
      this.#renderInvalidState();
      return;
    }

    this.removeAttribute('expired');
    this.#renderShell();
    this.#start();
  }

  #start() {
    this.#tick();
  }

  #stop() {
    if (this.#timeoutId !== undefined) {
      window.clearTimeout(this.#timeoutId);
      this.#timeoutId = undefined;
    }
  }

  #resolveTargetDate() {
    if (this.dataset.test === 'true') {
      return new Date(Date.now() + 5 * 1000);
    }

    if (this.dataset.demo === 'true') {
      return new Date(Date.now() + 24 * 60 * 60 * 1000);
    }

    return parseDateTime(this.dataset.datetime ?? '');
  }

  #tick = () => {
    if (!this.#targetDate) return;

    const parts = getRemainingParts(this.#targetDate);

    this.#renderParts(parts);
    this.#setAccessibleLabel(parts);
    this.toggleAttribute('expired', parts.totalSeconds <= 0);

    if (parts.totalSeconds <= 0) {
      this.#stop();
      this.#startEndTransition();
      return;
    }

    const delay = 1000 - (Date.now() % 1000);
    this.#timeoutId = window.setTimeout(this.#tick, delay);
  };

  #renderShell() {
    if (!this.#inner) return;

    this.#inner.textContent = '';
    this.#inner.classList.add('countdown-timer__inner');
    this.#inner.setAttribute('aria-hidden', 'true');
    this.#inner.style.removeProperty('min-height');

    this.#time = document.createElement('div');
    this.#time.className = 'countdown-timer__time';

    this.#valueElements = {};
    this.#lastValues = {};

    UNIT_CONFIGS.forEach((unit, index) => {
      if (index > 0) {
        this.#time?.append(createSeparator());
      }

      const item = document.createElement('div');
      const value = document.createElement('div');
      const label = document.createElement('div');

      item.className = `countdown-timer__item countdown-timer__item--${unit.key}`;
      value.className = 'timer__value';
      value.dataset.equalDigits = '';
      value.dataset.unit = unit.key;
      label.className = 'timer__label';
      label.textContent = unit.label;

      item.append(value, label);
      this.#time?.append(item);
      this.#valueElements[unit.key] = value;
    });

    this.#inner.append(this.#time);
  }

  #renderInvalidState() {
    this.#stop();

    if (!this.#inner) {
      this.#inner = this.querySelector('[ref="inner"]') ?? this;
    }

    this.#inner.textContent = '';
    this.#inner.classList.add('countdown-timer__inner');
    this.#inner.removeAttribute('aria-hidden');

    const message = document.createElement('div');
    message.className = 'countdown-timer__message';
    message.textContent = 'Invalid countdown datetime';

    this.#inner.append(message);
    this.setAttribute('aria-label', 'Invalid countdown datetime');
    this.toggleAttribute('invalid', true);
  }

  #startEndTransition() {
    if (this.#ending) return;

    this.#ending = true;

    const message = this.#createEndMessage();

    if (!this.#inner) {
      if (!message) this.hidden = true;
      return;
    }

    this.#inner.removeAttribute('aria-hidden');
    this.#inner.style.minHeight = `${this.#inner.getBoundingClientRect().height}px`;

    if (message) {
      this.#inner.append(message);
    } else {
      this.setAttribute('aria-label', 'Countdown ended');
    }

    const time = this.#time;
    let didFinish = false;
    const finish = () => {
      if (didFinish) return;

      didFinish = true;
      time?.remove();
      this.#time = null;

      if (message) {
        message.classList.remove('countdown-timer__message--entering');
        this.#inner?.style.removeProperty('min-height');
      } else {
        this.hidden = true;
      }
    };

    if (time) {
      time.classList.add('countdown-timer__time--leaving');
      time.addEventListener('animationend', finish, { once: true });
      window.setTimeout(finish, 700);
      return;
    }

    finish();
  }

  #createEndMessage() {
    const template = this.querySelector('template[data-countdown-end-message]');

    if (!(template instanceof HTMLTemplateElement) || !template.innerHTML.trim()) return null;

    const content = template.content.cloneNode(true);
    const message = document.createElement('div');

    message.className = 'countdown-timer__message countdown-timer__message--entering';
    message.append(content);

    this.setAttribute('aria-label', message.textContent?.trim() || 'Countdown ended');

    return message;
  }

  /**
   * @param {CountdownParts} parts
   */
  #renderParts(parts) {
    this.removeAttribute('invalid');

    for (const unit of UNIT_CONFIGS) {
      const value = parts[unit.key];

      if (this.#lastValues[unit.key] === value) continue;

      const valueElement = this.#valueElements[unit.key];

      if (!valueElement) continue;

      renderEqualDigitValue(valueElement, value);
      this.#lastValues[unit.key] = value;
    }

    this.#syncVisibleUnits(parts);
    this.#scheduleEqualize();
  }

  /**
   * @param {CountdownParts} parts
   */
  #syncVisibleUnits(parts) {
    if (!this.#time) return;

    const showDay = Number(parts.day) > 0;
    const dayItem = this.#time.querySelector('.countdown-timer__item--day');
    const firstSeparator = this.#time.querySelector('.countdown-timer__separator');

    if (dayItem instanceof HTMLElement) {
      dayItem.hidden = !showDay;
      dayItem.style.display = showDay ? '' : 'none';
    }

    if (firstSeparator instanceof HTMLElement) {
      firstSeparator.hidden = !showDay;
      firstSeparator.style.display = showDay ? '' : 'none';
    }
  }

  /**
   * @param {CountdownParts} parts
   */
  #setAccessibleLabel(parts) {
    this.setAttribute(
      'aria-label',
      `剩余 ${Number(parts.day)} 天 ${Number(parts.hour)} 小时 ${Number(parts.minute)} 分 ${Number(parts.second)} 秒`
    );
  }

  #scheduleEqualize = () => {
    if (this.#frameId !== undefined) {
      cancelAnimationFrame(this.#frameId);
    }

    this.#frameId = requestAnimationFrame(() => {
      this.#frameId = undefined;
      this.#syncContainerScale();
      this.#equalizeDigits();
      this.#syncTextBackground();
    });
  };

  #syncContainerScale() {
    const containerWidth = this.getBoundingClientRect().width;

    if (!containerWidth) return;

    const visibleUnits = UNIT_CONFIGS.filter((unit) => {
      const item = this.#time?.querySelector(`.countdown-timer__item--${unit.key}`);

      return !(item instanceof HTMLElement) || !item.hidden;
    });
    const totalDigitCount = visibleUnits.reduce((total, unit) => {
      return total + (this.#lastValues[unit.key]?.length || 0);
    }, 0);
    const totalWeight = totalDigitCount + SEPARATOR_WEIGHT * Math.max(0, visibleUnits.length - 1);

    if (!totalWeight) return;

    setStyleProperty(this, '--countdown-slot-size', `${containerWidth / totalWeight}px`);
  }

  #equalizeDigits() {
    const slotSize = getNumericCssValue(getComputedStyle(this).getPropertyValue('--countdown-slot-size'));

    if (!slotSize) return;

    const sampleElement = Object.values(this.#valueElements).find((element) => element instanceof HTMLElement);

    if (!sampleElement) return;

    const maxDigitWidth = getMaxDigitWidth(sampleElement);
    const digitFontSize = maxDigitWidth ? (slotSize * MEASURE_FONT_SIZE) / maxDigitWidth : slotSize;

    setStyleProperty(this, '--countdown-digit-font-size', `${digitFontSize}px`);
  }

  #syncTextBackground() {
    if (!this.#time) return;

    const timeRect = this.#time.getBoundingClientRect();

    if (!timeRect.width) return;

    setStyleProperty(this, '--countdown-text-background-width', `${timeRect.width}px`);

    this.#time.querySelectorAll('.timer__digit-glyph, .countdown-timer__separator').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      const elementRect = element.getBoundingClientRect();
      const offsetX = elementRect.left - timeRect.left;

      setStyleProperty(element, '--countdown-text-background-position-x', `${-offsetX}px`);
    });
  }
}

if (!customElements.get('jxs-countdown-timer')) {
  customElements.define('jxs-countdown-timer', JxsCountdownTimer);
}

/**
 * @param {HTMLElement} valueElement
 * @param {string} value
 */
function renderEqualDigitValue(valueElement, value) {
  valueElement.textContent = '';
  valueElement.setAttribute('aria-label', value);
  setStyleProperty(valueElement, '--digit-count', String(value.length));

  [...value].forEach((character) => {
    const slot = document.createElement('span');
    const glyph = document.createElement('span');

    slot.className = 'timer__digit-slot';
    slot.setAttribute('aria-hidden', 'true');

    glyph.className = 'timer__digit-glyph';
    glyph.textContent = character;

    slot.append(glyph);
    valueElement.append(slot);
  });

  const item = valueElement.closest('.countdown-timer__item');

  if (item instanceof HTMLElement) {
    setStyleProperty(item, '--digit-count', String(value.length));
  }
}

function createSeparator() {
  const separator = document.createElement('div');

  separator.className = 'countdown-timer__separator';
  separator.setAttribute('aria-hidden', 'true');
  separator.textContent = ':';

  return separator;
}

/**
 * @param {string} value
 * @returns {Date | null}
 */
function parseDateTime(value) {
  const datetime = value.trim();

  if (!datetime) return null;

  const matches = datetime.match(DEFAULT_DATETIME_PATTERN);

  if (matches) {
    const [, year, month, day, hour = '0', minute = '0', second = '0'] = matches;
    const date = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    );

    return Number.isNaN(date.getTime()) ? null : date;
  }

  const timestamp = Date.parse(datetime);

  return Number.isNaN(timestamp) ? null : new Date(timestamp);
}

/**
 * @param {Date} targetDate
 * @returns {CountdownParts}
 */
function getRemainingParts(targetDate) {
  const diff = Math.max(0, targetDate.getTime() - Date.now());
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / DAY_SECONDS);
  const hours = Math.floor((totalSeconds % DAY_SECONDS) / HOUR_SECONDS);
  const minutes = Math.floor((totalSeconds % HOUR_SECONDS) / MINUTE_SECONDS);
  const seconds = totalSeconds % MINUTE_SECONDS;

  return {
    day: String(days),
    hour: String(hours).padStart(2, '0'),
    minute: String(minutes).padStart(2, '0'),
    second: String(seconds).padStart(2, '0'),
    totalSeconds,
  };
}

/**
 * @param {HTMLElement} fromElement
 * @param {number} fontSize
 */
function copyFontStyle(fromElement, fontSize) {
  const computed = getComputedStyle(fromElement);

  FONT_PROPERTIES.forEach((property) => {
    measurer.style.setProperty(property, computed.getPropertyValue(property));
  });

  measurer.style.fontSize = `${fontSize}px`;
}

/**
 * @param {HTMLElement} fromElement
 */
function getMaxDigitWidth(fromElement) {
  copyFontStyle(fromElement, MEASURE_FONT_SIZE);

  return [...DIGIT_SAMPLE].reduce((maxWidth, character) => {
    measurer.textContent = character;

    return Math.max(maxWidth, measurer.getBoundingClientRect().width);
  }, 0);
}

/**
 * @param {HTMLElement} element
 * @param {string} property
 * @param {string} value
 */
function setStyleProperty(element, property, value) {
  if (element.style.getPropertyValue(property) !== value) {
    element.style.setProperty(property, value);
  }
}

/**
 * @param {string} value
 */
function getNumericCssValue(value) {
  const number = Number.parseFloat(value);

  return Number.isFinite(number) ? number : 0;
}